import React, { useState } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { useHeaderLinks } from '../../../../useHeaderLinks';
import { ChevronRightSquare } from '@withjoy/joykit/icons';
import type { BoxProps } from '@withjoy/joykit';
import { ButtonV2, Flex } from '@withjoy/joykit';
import type { HeaderLinksProps } from '../../../../AppNavBar.types';
import { animationTransition } from '@shared/utils/animationTransition';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { StyledList, StyledListItem, StyledBlogList, styles } from './DesktopMenu.styles';

type BlogLinkItem = NonNullable<ReturnType<typeof useHeaderLinks>['itemsToRender'][number]['blogs']>[number];

const BlogLink = ({ link }: Readonly<{ link: BlogLinkItem }>) => (
  <li>
    <ButtonV2
      as="a"
      intent="neutral"
      variant="ghost"
      typographyVariant="Hed56"
      href={link.href}
      target={link.target}
      onClick={link.onClick}
      textDecoration="none"
      cursor="pointer"
      fontFamily="Inter UI"
      fontWeight="medium"
      fontSize={pxToRem(16)}
      lineHeight={pxToRem(22)}
      letterSpacing="0.005em"
      height={pxToRem(22)}
      transition={animationTransition('color')}
      paddingX={0}
      _hover={{ color: 'buttonPrimaryHover' }}
      _focusVisible={{ color: 'buttonPrimaryHover' }}
    >
      {link.label}
    </ButtonV2>
  </li>
);

export const DesktopMenu = React.memo((props: HeaderLinksProps & BoxProps) => {
  const [selectedMenuId, setSelectedMenuId] = useState(-1);
  const isMobileOrTablet = useIsMobileOrTablet();
  const { type, getLinkProps, ...rootProps } = props;
  const { itemsToRender } = useHeaderLinks(type);
  const blogLinks = itemsToRender[selectedMenuId]?.blogs;

  return (
    <Flex
      flexDirection={['column', 'column', 'column', 'row']}
      style={{ gap: isMobileOrTablet ? 0 : pxToRem(48) }}
      paddingRight={6}
      {...getLinkProps?.()}
      {...rootProps}
    >
      <Flex flexDirection="column">
        <StyledList
          as="ul"
          __css={styles.list}
        >
          {itemsToRender.map((item, idx) => (
            <StyledListItem
              key={item.label}
              as="li"
              __css={styles.listItem}
            >
              <ButtonV2
                as="a"
                intent="neutral"
                variant="ghost"
                typographyVariant="Hed56"
                endIcon={item.blogs?.length && item.blogs.length > 0 ? <ChevronRightSquare marginTop={2} /> : null}
                href={item.href}
                target={item.target}
                onClick={item.onClick}
                onMouseEnter={event => {
                  event.preventDefault();
                  setSelectedMenuId(idx);
                }}
                textDecoration="none"
                cursor="pointer"
                fontFamily="Austin News Deck Web"
                fontWeight={300}
                fontSize={pxToRem(36)}
                lineHeight={pxToRem(40)}
                letterSpacing="-0.005em"
                height={pxToRem(40)}
                transition={animationTransition('color')}
                paddingX={0}
                _hover={{ color: 'buttonPrimaryHover' }}
                _focusVisible={{ color: 'buttonPrimaryHover' }}
              >
                {item.label}
              </ButtonV2>
            </StyledListItem>
          ))}
        </StyledList>
      </Flex>
      {blogLinks && (
        <Flex
          flexDirection={['column', 'column', 'column', 'row']}
          style={{ gap: isMobileOrTablet ? 0 : pxToRem(32) }}
        >
          <Flex flexDirection="column">
            <StyledBlogList
              as="ul"
              __css={styles.blogList}
            >
              {blogLinks.slice(0, 6).map(x => (
                <BlogLink
                  key={x.label}
                  link={x}
                />
              ))}
            </StyledBlogList>
          </Flex>
          {blogLinks.length > 6 && (
            <Flex flexDirection="column">
              <StyledBlogList
                as="ul"
                __css={styles.blogList}
              >
                {blogLinks.slice(6).map(x => (
                  <BlogLink
                    key={x.label}
                    link={x}
                  />
                ))}
              </StyledBlogList>
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
});
